// Form structure table columns — one row per FIELD, across every form template.
//
// EVERY field fetch-structure returns gets a column, bar `form_area`. That
// includes both halves of the two pairs the wire sends separately: section
// number AND section title, page number AND page title. Neither is folded into
// the other, because the table's job here is to show what the warehouse row
// will hold.
//
// Two of the columns are EDITABLE and dynamic: Topics (always present) and one
// per question set that materialised as a key on the response. Both render
// SetValueCell over a catalogue — the row carries a TITLE and the save needs an
// id, so the catalogue is the lookup, not just a source of labels. The tab owns
// the drafts and does the resolving; this file only renders.
//
// Seventeen columns is wide on purpose — the table hugs and scrolls, the standing
// no-compression rule, and the Columns menu is how a reader narrows it.
//
// NOTHING here declares `sortable`, and there is no search or filter on this
// tab either. POST /dashboard-setup/fetch-structure takes `{ page, limit }` and
// nothing else — the design draws sort glyphs on ten of these headers, but a
// control the endpoint cannot honour is worse than an absent one. Add flags
// (and an `onSort`) only once the payload names its accepted keys, and declare
// those keys as an enum rather than free strings.
//
// Both id columns hold Mongo ObjectIds, so they are wide. They still render
// literally in the monospace pill — they are data, not copy — and `dir='ltr'`
// keeps them latin under Arabic. The table hugs and scrolls rather than
// squeezing them.
//
// THREE fr-floored columns, not one: the form title, the field title and the
// hint text. The field title is the row's SUBJECT and in practice the longest
// free text here, so leaving it on an auto track would let one long question
// widen the whole grid and push every other column out of view. Everything else
// hugs its content — the standing no-compression rule.
//
// `dash` is a local copy on purpose: every *Columns file in this repo defines
// its own.

import { useTranslation } from 'react-i18next';
import { Icon, type DataTableColumn } from '@/components/common';
import type { FormStructureField } from '@/api/endpoints/dataSetupApi';
import type { OptionSetSelection } from '@/api/endpoints/formSetsApi';
import type { TopicSelection } from '@/api/endpoints/topicSetsApi';
import SetValueCell from './SetValueCell';

const dash = <span className='data-setup-table__muted'>—</span>;

/** A flag as glyph + word. Both arms are icons — a text em-dash next to a
 *  stroked check has a different baseline, and three boolean columns of that
 *  read ragged. The icon is decorative; the word carries the meaning. */
const BoolCell = ({ on }: { on: boolean }) => {
  const { t } = useTranslation();
  return (
    <span className={`data-setup-table__bool${on ? ' is-on' : ''}`}>
      <Icon name={on ? 'check' : 'minus'} size={14} aria-hidden='true' />
      {t(on ? 'data_setup_structure_true' : 'data_setup_structure_false')}
    </span>
  );
};

/** Column key for the always-on Topics column, and for one question set.
 *  Prefixed so neither can collide with a fixed column's key. */
export const TOPIC_COLUMN_KEY = 'dyn:topic';
export const questionSetColumnKey = (sqlColumn: string) => `dyn:${sqlColumn}`;

interface UseFormStructureColumnsArgs {
  /** The topic catalogue — always one column, even when empty. */
  topics: TopicSelection[];
  /** Question sets that actually materialised as keys on the response rows.
   *  The tab does the intersecting; this just renders what it is handed. */
  questionSets: OptionSetSelection[];
  /** Resolved option id for a row+column — a draft if touched, else the row's
   *  own saved pick mapped through the catalogue. '' for nothing picked. */
  valueOf: (row: FormStructureField, columnKey: string) => string;
  onValueCommit: (rowId: string, columnKey: string, optionId: string) => void;
}

export const useFormStructureColumns = ({
  topics,
  questionSets,
  valueOf,
  onValueCommit,
}: UseFormStructureColumnsArgs): DataTableColumn<FormStructureField>[] => {
  const { t } = useTranslation();

  return [
    {
      key: 'formId',
      label: t('data_setup_structure_col_form_id'),
      render: (f) =>
        f.formId ? (
          <code className='data-setup-table__sql' dir='ltr'>
            {f.formId}
          </code>
        ) : (
          dash
        ),
    },
    {
      key: 'formTitle',
      label: t('data_setup_structure_col_form'),
      // Context rather than subject, so it yields to the field title.
      width: 'minmax(11rem, 1fr)',
      render: (f) => (
        <span className='data-setup-table__name'>
          <span className='data-setup-table__tile' aria-hidden='true'>
            <Icon name='clipboard-list' size={18} />
          </span>
          <span className='data-setup-table__title'>{f.formTitle || '—'}</span>
        </span>
      ),
    },
    {
      key: 'fieldTitle',
      label: t('data_setup_structure_col_field_title'),
      // The row's subject — the widest share of the flexible space.
      width: 'minmax(12rem, 1.4fr)',
      render: (f) =>
        f.fieldTitle ? (
          // Truncated, so the full text has to stay reachable somehow.
          <span className='data-setup-table__field-title' title={f.fieldTitle}>
            {f.fieldTitle}
          </span>
        ) : (
          dash
        ),
    },
    {
      key: 'fieldId',
      label: t('data_setup_structure_col_field_id'),
      render: (f) =>
        f.fieldId ? (
          <code className='data-setup-table__sql' dir='ltr'>
            {f.fieldId}
          </code>
        ) : (
          dash
        ),
    },
    {
      key: 'elementGlobalId',
      label: t('data_setup_structure_col_element_id'),
      // `id` IS element_global_id — see FormStructureField. Rendered rather
      // than duplicated onto the domain row.
      render: (f) => (
        <code className='data-setup-table__sql' dir='ltr'>
          {f.id}
        </code>
      ),
    },
    {
      key: 'required',
      label: t('data_setup_structure_col_required'),
      render: (f) => <BoolCell on={f.required} />,
    },
    {
      key: 'hidden',
      label: t('data_setup_structure_col_hidden'),
      render: (f) => <BoolCell on={f.hidden} />,
    },
    {
      key: 'hintText',
      label: t('data_setup_structure_col_hint'),
      width: 'minmax(10rem, 1fr)',
      render: (f) =>
        f.hintText ? (
          <span className='data-setup-table__hint' title={f.hintText}>
            {f.hintText}
          </span>
        ) : (
          dash
        ),
    },
    {
      key: 'scoring',
      label: t('data_setup_structure_col_scoring'),
      render: (f) => <BoolCell on={f.scoring} />,
    },
    {
      key: 'fieldType',
      label: t('data_setup_structure_col_field_type'),
      // Verbatim, and inconsistently cased at source: `TEXT FIELD`,
      // `companystructure` and `Slider` all arrive in one column. Not
      // normalised here — there is no canonical vocabulary to map against
      // (forms' own `elementType` is a bare string too), so a mapping would be
      // invented and a new element type would render as the wrong thing.
      render: (f) => f.fieldType || dash,
    },
    {
      key: 'section',
      label: t('data_setup_structure_col_section'),
      // The number. Its title is its own column — the wire sends both and the
      // table shows both rather than folding one into the other.
      render: (f) => f.section || dash,
    },
    {
      key: 'sectionTitle',
      label: t('data_setup_structure_col_section_title'),
      render: (f) => f.sectionTitle || dash,
    },
    {
      key: 'page',
      label: t('data_setup_structure_col_page'),
      // The form's page, not pagination.
      render: (f) => f.pageNumber || dash,
    },
    {
      key: 'pageTitle',
      label: t('data_setup_structure_col_page_title'),
      render: (f) => f.pageTitle || dash,
    },
    {
      key: 'folder',
      label: t('data_setup_structure_col_folder'),
      render: (f) => f.folder || dash,
    },
    {
      key: 'maxScore',
      label: t('data_setup_structure_col_max_score'),
      // "OPEN" is a sentinel for an unscored field, not a number — shown as
      // sent rather than translated, like every other value in this table.
      render: (f) => f.maxScore || dash,
    },
    {
      key: 'triggeredBy',
      label: t('data_setup_structure_col_triggered_by'),
      // The raw field id: the field it names may sit on another page of
      // results, so it cannot be resolved to a title from the loaded rows.
      render: (f) =>
        f.triggeredByFieldId ? (
          <code className='data-setup-table__sql' dir='ltr'>
            {f.triggeredByFieldId}
          </code>
        ) : (
          dash
        ),
    },
    // ---- dynamic columns ----
    // Topics first and unconditionally; then one per question set that actually
    // appeared on the response. Both are editable, and both are FIXED tracks: a
    // native select's min-content is its longest option, so an `auto` track
    // would let one long option widen the whole table.
    {
      key: TOPIC_COLUMN_KEY,
      label: t('data_setup_structure_col_topic'),
      width: '12rem',
      render: (f) => (
        <SetValueCell
          rowId={f.id}
          rowLabel={f.fieldTitle ?? f.fieldId}
          groupLabel={t('data_setup_structure_col_topic')}
          options={topics.map((tp) => ({ id: tp.id, value: tp.title }))}
          value={valueOf(f, TOPIC_COLUMN_KEY)}
          onChange={(rowId, optionId) => onValueCommit(rowId, TOPIC_COLUMN_KEY, optionId)}
        />
      ),
    },
    ...questionSets.map<DataTableColumn<FormStructureField>>((qs) => {
      const key = questionSetColumnKey(qs.sqlColumn.toLowerCase());
      return {
        key,
        label: qs.title,
        width: '12rem',
        render: (f) => (
          <SetValueCell
            rowId={f.id}
            rowLabel={f.fieldTitle ?? f.fieldId}
            groupLabel={qs.title}
            options={qs.options}
            value={valueOf(f, key)}
            onChange={(rowId, optionId) => onValueCommit(rowId, key, optionId)}
          />
        ),
      };
    }),
  ];
};

export default useFormStructureColumns;
